/* Copyright (c) 2024 Xperi. All Rights Reserved. */

/**
 * This file implements all the functions for nDVR feature,
 * which are exposed from the data/domain layer to other layers.
 * Any modifications to the exposed functions can be made here
 */

import { ContentScreenDetails } from 'client-code/actions';
import { SeasonOrYear } from 'client-code/bookmarks';
import { SeasonsData } from 'client-code/episode-guide';
import { MyShowsFilterList } from 'client-code/myshows';
import { OnePassValues } from 'client-code/onepass';
import { ScreenType } from 'client-code/previews';
import {
    MyShowsUserSettings,
    MyShowsUserSettingsValue,
    OnePassAndRecordingsUserSettings,
    OnePassAndRecordingsUserSettingsValues,
} from 'client-code/user-settings';

import { TIVO_SERVICES } from '../../config';
import { DeviceFeatures, DeviceModel } from '../device';
import {
    ActionsService,
    BookmarkService,
    EpisodeGuideService,
    MyShowsService,
    OnePassService,
    PreviewsService,
    RecordingsService,
    RecordingsStatusService,
    Services,
    UpcomingOffersService,
    UserSettingsService,
    WatchStickinessService,
} from '../infrastructure';
import { TiVoNetworkRequestModel } from '../tivo';
import {
    TActionsContentScreenContentIdParams,
    TActionsContentScreenOfferParams,
    TActionsContentScreenRecordingParams,
    TActionsParamsUCScreenDetails,
    TCollectionActivityStoreParams,
    TEpisodeInfo,
    TEpisodeParams,
    TMyShowsItemList,
    TMyShowsItemListParams,
    TOfferPreview,
    TOnePassList,
    TOnePassListParams,
    TOnePassOptions,
    TOnePassPreviewItem,
    TOnePassUCIdPreivewParams,
    TOnePassValuesWithDisplayOptions,
    TRecordingOptions,
    TRecordingValues,
    TRecordingValuesWithDisplayOptions,
    TSeasonsParams,
    TStorageInfo,
    TUnifiedCollectionPreview,
    TUnifiedCollectionPreviewParams,
    TUnifiedCollectionScreenDetails,
    TUpcomingOffersList,
    TUpcomingOffersParams,
    TUpcomingOffersV2Params,
} from '../types';
import { ServicesClientImpl } from './ServicesClientImpl';

export class NdvrInteractor extends ServicesClientImpl {
    /**
     * A cache for storing offer preview information.
     * The key is the offer ID, and the value is the corresponding offer preview information.
     */
    private static mOfferPreviewCache: Map<string, TOfferPreview> = new Map();

    // eslint-disable-next-line class-methods-use-this
    private _commonParameters() {
        return {
            accessToken: DeviceModel.getAccessToken(),
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        };
    }

    private _commonParametersWithTimeZone() {
        return {
            ...this._commonParameters(),
            timeZone: DeviceModel.getTVProperties().getDefaultTimeZone(),
        };
    }

    // eslint-disable-next-line class-methods-use-this
    public getServicesBaseUrls() {
        return NdvrInteractor.servicesBaseUrls;
    }

    /**
     * Below are the method related to cloudcore-onepass
     */

    public async getOnePassList(onePassListParams: TOnePassListParams): Promise<TOnePassList> {
        return Services.getInstanceOf(OnePassService, {
            ...this._commonParametersWithTimeZone(),
            basePath: NdvrInteractor.servicesBaseUrls[TIVO_SERVICES.OnePassService],
        }).getOnePassList(onePassListParams);
    }

    public async getOnePassOptions(
        unifiedCollectionId: string,
        networkCallId?: number,
    ): Promise<TOnePassOptions> {
        return Services.getInstanceOf(OnePassService, {
            ...this._commonParametersWithTimeZone(),
            basePath: NdvrInteractor.servicesBaseUrls[TIVO_SERVICES.OnePassService],
        }).getOnePassOptions(unifiedCollectionId, networkCallId);
    }

    public createOnePass(
        unifiedCollectionId: string,
        onePassValues: OnePassValues,
        networkCallId?: number | undefined,
    ): Promise<void> {
        return Services.getInstanceOf(OnePassService, {
            ...this._commonParametersWithTimeZone(),
            basePath: NdvrInteractor.servicesBaseUrls[TIVO_SERVICES.OnePassService],
        }).createOnePass(unifiedCollectionId, onePassValues, networkCallId);
    }

    public deleteOnePass(
        unifiedCollectionId: string,
        networkCallId?: number | undefined,
    ): Promise<void> {
        return Services.getInstanceOf(OnePassService, {
            ...this._commonParametersWithTimeZone(),
            basePath: NdvrInteractor.servicesBaseUrls[TIVO_SERVICES.OnePassService],
        }).deleteOnePass(unifiedCollectionId, networkCallId);
    }

    public getValuesWithDisplayOptions(
        unifiedCollectionId: string,
        networkCallId?: number | undefined,
    ): Promise<TOnePassValuesWithDisplayOptions> {
        return Services.getInstanceOf(OnePassService, {
            ...this._commonParametersWithTimeZone(),
            basePath: NdvrInteractor.servicesBaseUrls[TIVO_SERVICES.OnePassService],
        }).getValuesWithDisplayOptions(unifiedCollectionId, networkCallId);
    }

    public updateOnePass(
        unifiedCollectionId: string,
        onePassValues: OnePassValues,
        networkCallId?: number | undefined,
    ): Promise<void> {
        return Services.getInstanceOf(OnePassService, {
            ...this._commonParametersWithTimeZone(),
            basePath: NdvrInteractor.servicesBaseUrls[TIVO_SERVICES.OnePassService],
        }).updateOnePass(unifiedCollectionId, onePassValues, networkCallId);
    }

    /**
     * Retrieves the offer preview information for the given offer ID.
     * If the information is not available locally, it fetches it from the server.
     *
     * @param offerId - The ID of the offer for which to retrieve the preview information.
     * @returns A promise that resolves to an object containing the offer preview information.
     */
    public async getOrFetchOfferPreviewInfo(offerId: string): Promise<TOfferPreview> {
        if (!NdvrInteractor.mOfferPreviewCache.has(offerId)) {
            NdvrInteractor.mOfferPreviewCache.clear();
            const offerPreview = await this.fetchOfferPreview(offerId, ScreenType.All);
            NdvrInteractor.mOfferPreviewCache.set(offerId, offerPreview);
        }
        return NdvrInteractor.mOfferPreviewCache.get(offerId) as TOfferPreview;
    }

    /**
     * Below are the method related to episode-guide
     */

    public clearCachedOffers(): void {
        Services.getInstanceOf(EpisodeGuideService, {
            ...this._commonParametersWithTimeZone(),
            basePath: NdvrInteractor.servicesBaseUrls[TIVO_SERVICES.EpisodeGuideService],
            msoName: DeviceModel.getTiVoSettings().getMsoName(),
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).clearCachedOffers();
    }

    public clearSeasonsData(): void {
        Services.getInstanceOf(EpisodeGuideService, {
            ...this._commonParametersWithTimeZone(),
            basePath: NdvrInteractor.servicesBaseUrls[TIVO_SERVICES.EpisodeGuideService],
            msoName: DeviceModel.getTiVoSettings().getMsoName(),
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).clearSeasonsData();
    }

    public getEpisodes(episodeParams: TEpisodeParams): Promise<TEpisodeInfo> {
        return Services.getInstanceOf(EpisodeGuideService, {
            ...this._commonParametersWithTimeZone(),
            basePath: NdvrInteractor.servicesBaseUrls[TIVO_SERVICES.EpisodeGuideService],
            msoName: DeviceModel.getTiVoSettings().getMsoName(),
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).getEpisodes(episodeParams);
    }

    public getSeasonData(seasonsParams: TSeasonsParams): Promise<SeasonsData> {
        return Services.getInstanceOf(EpisodeGuideService, {
            ...this._commonParametersWithTimeZone(),
            basePath: NdvrInteractor.servicesBaseUrls[TIVO_SERVICES.EpisodeGuideService],
            msoName: DeviceModel.getTiVoSettings().getMsoName(),
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).getSeasonData(seasonsParams);
    }

    /**
     * Below are the method related to cloudcore-myshows
     */

    public getDiskSpace = (networkCallId?: number): Promise<TStorageInfo> => {
        return Services.getInstanceOf(MyShowsService, {
            ...this._commonParameters(),
            basePath: NdvrInteractor.servicesBaseUrls[TIVO_SERVICES.MyShows],
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).getDiskSpace(networkCallId);
    };

    public getMyShowsFilterList(): Promise<MyShowsFilterList> {
        return Services.getInstanceOf(MyShowsService, {
            ...this._commonParameters(),
            basePath: NdvrInteractor.servicesBaseUrls[TIVO_SERVICES.MyShows],
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).getMyShowsFilterList();
    }

    public getMyShowsItemList({
        myShowsFilter,
        notCurrentlyAvailable,
        recentlyDeleted,
        streamingMovies,
        count,
        offset,
        orderBy,
        unifiedCollectionId,
    }: TMyShowsItemListParams): Promise<TMyShowsItemList> {
        return Services.getInstanceOf(MyShowsService, {
            ...this._commonParametersWithTimeZone(),
            basePath: NdvrInteractor.servicesBaseUrls[TIVO_SERVICES.MyShows],
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).getMyShowsItemList({
            myShowsFilter,
            notCurrentlyAvailable,
            recentlyDeleted,
            streamingMovies,
            count,
            offset,
            orderBy,
            unifiedCollectionId,
        });
    }

    public setLastSelectedItem(myShowsItemId: string) {
        return Services.getInstanceOf(MyShowsService, {
            ...this._commonParameters(),
            basePath: NdvrInteractor.servicesBaseUrls[TIVO_SERVICES.MyShows],
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).setLastSelectedItem(myShowsItemId);
    }

    /**
     * Below are the method related to cloudcore-user-settings
     */

    public getMyShowsUserSettings(): Promise<MyShowsUserSettings> {
        return Services.getInstanceOf(UserSettingsService, {
            ...this._commonParameters(),
            basePath: NdvrInteractor.servicesBaseUrls[TIVO_SERVICES.UserSettingsService],
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).getMyShowsUserSettings();
    }

    public setMyShowsUserSettings(value: MyShowsUserSettingsValue) {
        return Services.getInstanceOf(UserSettingsService, {
            ...this._commonParameters(),
            basePath: NdvrInteractor.servicesBaseUrls[TIVO_SERVICES.UserSettingsService],
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).setMyShowsUserSettings(value);
    }

    public getOnepassAndRecordingsUserSettings(): Promise<OnePassAndRecordingsUserSettings> {
        return Services.getInstanceOf(UserSettingsService, {
            ...this._commonParameters(),
            basePath: NdvrInteractor.servicesBaseUrls[TIVO_SERVICES.UserSettingsService],
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).getOnePassAndRecordingsUserSettings();
    }

    public setOnePassAndRecordingsUserSettings(value: OnePassAndRecordingsUserSettingsValues) {
        return Services.getInstanceOf(UserSettingsService, {
            ...this._commonParameters(),
            basePath: NdvrInteractor.servicesBaseUrls[TIVO_SERVICES.UserSettingsService],
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).setOnePassAndRecordingsUserSettings(value);
    }

    /**
     * Below are the method related to guide-recordings-status
     */

    public getRecordingsStatus() {
        return Services.getInstanceOf(RecordingsStatusService, {
            ...this._commonParameters(),
            basePath: NdvrInteractor.servicesBaseUrls[TIVO_SERVICES.RecordingsStatusService],
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).getRecordingsStatus();
    }

    public getRecordingsStatusDetails() {
        return Services.getInstanceOf(RecordingsStatusService, {
            ...this._commonParameters(),
            basePath: NdvrInteractor.servicesBaseUrls[TIVO_SERVICES.RecordingsStatusService],
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).getRecordingsStatusDetails();
    }

    /**
     * Below are the method related to cloudcore-bookmarks
     */

    /**
     * The method will return the object `TiVoNetworkRequestModel` to `create/schedule` the
     * bookmark for the specified contentId.
     * @param contentId (string) - The `contentId of the <episode/game/match/event>
     * @param unifiedCollectionId (string) - The `unifiedCollectionId` of the
     *  <episode/game/match/event/race/movie>
     * @returns `TiVoNetworkRequestModel<any>`
     */
    public getRequestModelToCreateBookmarkContentIdSchedule(
        contentId: string,
        unifiedCollectionId: string,
    ): TiVoNetworkRequestModel<any> {
        return Services.getInstanceOf(BookmarkService, {
            ...this._commonParametersWithTimeZone(),
            basePath: NdvrInteractor.servicesBaseUrls[TIVO_SERVICES.BookmarkService],
        }).getRequestModelToCreateBookmarkContentIdSchedule(contentId, unifiedCollectionId);
    }

    /**
     * The method will return the object `TiVoNetworkRequestModel` to `create/schedule` the
     * bookmark for the specified collectionId.
     * @param collectionId (string) - The `collectionId` of the
     *  <episode/game/match/event/race/movie>
     * @param seasonOrYear (SeasonOrYear) - The `Season` or `Year` of the
     *  <episode/game/match/event/race/movie>
     * @returns `TiVoNetworkRequestModel<any>`
     */
    public getRequestModelToCreateBookmarkCollectionIdSchedule(
        collectionId: string,
        seasonOrYear: SeasonOrYear,
    ): TiVoNetworkRequestModel<any> {
        return Services.getInstanceOf(BookmarkService, {
            ...this._commonParametersWithTimeZone(),
            basePath: NdvrInteractor.servicesBaseUrls[TIVO_SERVICES.BookmarkService],
        }).getRequestModelToCreateBookmarkCollectionIdSchedule(collectionId, seasonOrYear);
    }

    /**
     * The method will return the object `TiVoNetworkRequestModel` to `delete` the existing bookmark
     * for the specified contentId
     * @param contentId (string) - The `contentId` of the <episode/game/match/event/race/movie>
     */
    public getRequestModelToDeleteBookmarkSchedule(
        contentId: string,
    ): TiVoNetworkRequestModel<any> {
        return Services.getInstanceOf(BookmarkService, {
            ...this._commonParametersWithTimeZone(),
            basePath: NdvrInteractor.servicesBaseUrls[TIVO_SERVICES.BookmarkService],
        }).getRequestModelToDeleteBookmarkSchedule(contentId);
    }

    /**
     * Below are the method related to cloudcore-recordings
     */

    /**
     * The method will return the object `TiVoNetworkRequestModel` to `cancel` the in progress
     * recording offerId schedule.
     * @param contentId (string) - The `contentId of the <episode/game/match/event>
     * @param offerId (string) - The `offerId` of the <episode/game/match/event>
     * @returns `TiVoNetworkRequestModel<any>`
     */
    // eslint-disable-next-line class-methods-use-this
    public getRequestModelToCancelRecordingOfferIdSchedule(
        contentId: string,
        offerId: string,
    ): TiVoNetworkRequestModel<any> {
        return Services.getInstanceOf(RecordingsService, {
            ...this._commonParameters(),
            timeZone: DeviceModel.getTVProperties().getOlsonTimeZone(),
            basePath: NdvrInteractor.servicesBaseUrls[TIVO_SERVICES.RecordingsService],
        }).getRequestModelToCancelRecordingOfferIdSchedule(contentId, offerId);
    }

    /**
     * The method will return the object `TiVoNetworkRequestModel` to `create/schedule` the
     * recording offerId schedule.
     * @param contentId (string) - The `contentId of the <episode/game/match/event>
     * @param offerId (string) - The `offerId` of the <episode/game/match/event>
     * @param tRecordingValues (TRecordingValues) - The `tRecordingValues` of the `contentId`
     * and `recordingId`
     * @returns `TiVoNetworkRequestModel<any>`
     */
    // eslint-disable-next-line class-methods-use-this
    public getRequestModelToCreateRecordingOfferIdSchedule(
        contentId: string,
        offerId: string,
        tRecordingValues: TRecordingValues,
    ): TiVoNetworkRequestModel<any> {
        return Services.getInstanceOf(RecordingsService, {
            ...this._commonParameters(),
            timeZone: DeviceModel.getTVProperties().getOlsonTimeZone(),
            basePath: NdvrInteractor.servicesBaseUrls[TIVO_SERVICES.RecordingsService],
        }).getRequestModelToCreateRecordingOfferIdSchedule(contentId, offerId, tRecordingValues);
    }

    /**
     * The method will return the object `TiVoNetworkRequestModel` to `delete` the existing or in
     * progress recording schedule.
     * @param recordingId (string) - The `recordingId` of the <episode/game/match/event>
     */
    // eslint-disable-next-line class-methods-use-this
    public getRequestModelToDeleteRecordingSchedule(
        recordingId: string,
    ): TiVoNetworkRequestModel<any> {
        return Services.getInstanceOf(RecordingsService, {
            ...this._commonParameters(),
            timeZone: DeviceModel.getTVProperties().getOlsonTimeZone(),
            basePath: NdvrInteractor.servicesBaseUrls[TIVO_SERVICES.RecordingsService],
        }).getRequestModelToDeleteRecordingSchedule(recordingId);
    }

    /**
     * The method will return the object `TiVoNetworkRequestModel` to get `TRecordingOptions`.
     * @param contentId (string) - The `contentId of the <episode/game/match/event>
     * @param offerId (string) - The `offerId` of the <episode/game/match/event>
     * @returns `TiVoNetworkRequestModel<TRecordingOptions>`
     */
    // eslint-disable-next-line class-methods-use-this
    public getRecordingOfferIdOptionsRequestModel(
        contentId: string,
        offerId: string,
    ): TiVoNetworkRequestModel<TRecordingOptions> {
        return Services.getInstanceOf(RecordingsService, {
            ...this._commonParameters(),
            timeZone: DeviceModel.getTVProperties().getOlsonTimeZone(),
            basePath: NdvrInteractor.servicesBaseUrls[TIVO_SERVICES.RecordingsService],
        }).getRequestModelForOfferIdRecordingOptions(contentId, offerId);
    }

    /**
     * The method will return the object `TiVoNetworkRequestModel` to get existing or default
     * `TRecordingValuesWithDisplayOptions`.
     * @param contentId (string) - The `contentId of the <episode/game/match/event>
     * @param offerId (string) - The `offerId` of the <episode/game/match/event>
     * @returns `TiVoNetworkRequestModel<TRecordingValuesWithDisplayOptions>`
     */
    // eslint-disable-next-line class-methods-use-this
    public getRecordingOfferIdScheduleRequestModel(
        contentId: string,
        offerId: string,
    ): TiVoNetworkRequestModel<TRecordingValuesWithDisplayOptions> {
        return Services.getInstanceOf(RecordingsService, {
            ...this._commonParameters(),
            timeZone: DeviceModel.getTVProperties().getOlsonTimeZone(),
            basePath: NdvrInteractor.servicesBaseUrls[TIVO_SERVICES.RecordingsService],
        }).getRequestModelForRecordingValuesWithDisplayOptions(contentId, offerId);
    }

    /**
     * The method will return the object `TiVoNetworkRequestModel` to get the `TRecordingOptions`
     * for existing recording.
     * @param contentId (string) - The `contentId of the <episode/game/match/event>
     * @param recordingId (string) - The `recordingId` of the <episode/game/match/event>
     * @returns `TiVoNetworkRequestModel<TRecordingValues>`
     */
    // eslint-disable-next-line class-methods-use-this
    public getRecordingOptionsRequestModel(
        contentId: string,
        recordingId: string,
    ): TiVoNetworkRequestModel<TRecordingOptions> {
        return Services.getInstanceOf(RecordingsService, {
            ...this._commonParameters(),
            timeZone: DeviceModel.getTVProperties().getOlsonTimeZone(),
            basePath: NdvrInteractor.servicesBaseUrls[TIVO_SERVICES.RecordingsService],
        }).getRequestModelForRecordingIdRecordingOptions(contentId, recordingId);
    }

    /**
     * The method will return the object `TiVoNetworkRequestModel` to get the existing
     * `TRecordingValues` for the `recordingId`.
     * @param contentId (string) - The `contentId of the <episode/game/match/event>
     * @param recordingId (string) - The `recordingId` of the <episode/game/match/event>
     * @returns `TiVoNetworkRequestModel<TRecordingValues>`
     */
    // eslint-disable-next-line class-methods-use-this
    public getRecordingScheduleRequestModel(
        contentId: string,
        recordingId: string,
    ): TiVoNetworkRequestModel<TRecordingValues> {
        return Services.getInstanceOf(RecordingsService, {
            ...this._commonParameters(),
            timeZone: DeviceModel.getTVProperties().getOlsonTimeZone(),
            basePath: NdvrInteractor.servicesBaseUrls[TIVO_SERVICES.RecordingsService],
        }).getRequestModelForRecordingValues(contentId, recordingId);
    }

    /**
     * The method will return the object `TiVoNetworkRequestModel` to `fetch` the Last
     * Viewed Position of the recording.
     * @param recordingId (string) - The `recordingId` of the <episode/game/match/event>
     * @returns `TiVoNetworkRequestModel<any>`
     */
    // eslint-disable-next-line class-methods-use-this
    public getRequestModelToFetchRecordingsLastViewedPosition(
        recordingId: string,
    ): TiVoNetworkRequestModel<any> {
        return Services.getInstanceOf(RecordingsService, {
            ...this._commonParameters(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.RecordingsService],
            timeZone: DeviceModel.getTVProperties().getOlsonTimeZone(),
        }).getRequestModelToFetchRecordingsLastViewedPosition(recordingId);
    }

    /**
     * The method will return the object `TiVoNetworkRequestModel` to `recover` a recently
     * deleted recording.
     * @param recordingId (string) - The `recordingId` of the <episode/game/match/event>
     * @returns `TiVoNetworkRequestModel<any>`
     */
    // eslint-disable-next-line class-methods-use-this
    public getRequestModelToRecordingRecovery(recordingId: string): TiVoNetworkRequestModel<any> {
        return Services.getInstanceOf(RecordingsService, {
            ...this._commonParameters(),
            basePath: NdvrInteractor.servicesBaseUrls[TIVO_SERVICES.RecordingsService],
            timeZone: DeviceModel.getTVProperties().getOlsonTimeZone(),
        }).getRequestModelToRecordingRecovery(recordingId);
    }

    /**
     * The method will return the object `TiVoNetworkRequestModel` to update the Last
     * Viewed Postion of the recording.
     * It is the interface method to the service endpoint:
     * `/v1/recordings/recordingId/{recordingId}/lastViewedPosition - PUT`
     * @param recordingId (string) - The `recordingId` of the <episode/game/match/event>
     * @param lastViewedPosition (number) - The `lastViewedPosition` of the `recording`
     */
    // eslint-disable-next-line class-methods-use-this
    public getRequestModelToUpdateRecordingsLastViewedPosition(
        recordingId: string,
        lastViewedPosition: number,
    ): TiVoNetworkRequestModel<any> {
        return Services.getInstanceOf(RecordingsService, {
            ...this._commonParameters(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.RecordingsService],
            timeZone: DeviceModel.getTVProperties().getOlsonTimeZone(),
        }).getRequestModelToUpdateRecordingsLastViewedPosition(recordingId, lastViewedPosition);
    }

    /**
     * The method will return the object `TiVoNetworkRequestModel` to `update` the recording values
     * for the `offerId`.
     * @param contentId (string) - The `contentId of the <episode/game/match/event>
     * @param offerId (string) - The `offerId` of the <episode/game/match/event>
     * @param tRecordingValues (TRecordingValues) - The `tRecordingValues` of the `contentId`
     * and `recordingId`.
     * @returns `TiVoNetworkRequestModel<any>`
     */
    // eslint-disable-next-line class-methods-use-this
    public getRequestModelToUpdateRecordingOfferIdSchedule(
        contentId: string,
        offerId: string,
        tRecordingValues: TRecordingValues,
    ): TiVoNetworkRequestModel<any> {
        return Services.getInstanceOf(RecordingsService, {
            ...this._commonParametersWithTimeZone(),
            basePath: NdvrInteractor.servicesBaseUrls[TIVO_SERVICES.RecordingsService],
            timeZone: DeviceModel.getTVProperties().getOlsonTimeZone(),
        }).getRequestModelToUpdateRecordingOfferIdSchedule(contentId, offerId, tRecordingValues);
    }

    /**
     * The method will return the object `TiVoNetworkRequestModel` to `update` the recording values
     * for the existing `recordingId`.
     * @param contentId (string) - The `contentId of the <episode/game/match/event>
     * @param recordingId (string) - The `recordingId` of the <episode/game/match/event>
     * @param tRecordingValues (TRecordingValues) - The `tRecordingValues` of the `contentId`
     * and `recordingId`.
     * @returns `TiVoNetworkRequestModel<any>`
     */
    // eslint-disable-next-line class-methods-use-this
    public getRequestModelToUpdateRecordingSchedule(
        contentId: string,
        recordingId: string,
        tRecordingValues: TRecordingValues,
    ): TiVoNetworkRequestModel<any> {
        return Services.getInstanceOf(RecordingsService, {
            ...this._commonParametersWithTimeZone(),
            basePath: NdvrInteractor.servicesBaseUrls[TIVO_SERVICES.RecordingsService],
            timeZone: DeviceModel.getTVProperties().getOlsonTimeZone(),
        }).getRequestModelToUpdateRecordingSchedule(contentId, recordingId, tRecordingValues);
    }

    /**
     * Below are the method related to upcoming-offers
     */

    public getUpcomingOffersForCollectionId(
        upcomingOffersParams: TUpcomingOffersParams,
    ): Promise<TUpcomingOffersList> {
        return Services.getInstanceOf(UpcomingOffersService, {
            ...this._commonParameters(),
            basePath: NdvrInteractor.servicesBaseUrls[TIVO_SERVICES.UpcomingOffersService],
            imageBaseUrl: NdvrInteractor.servicesBaseUrls[TIVO_SERVICES.ImageHost],
            channelBaseUrl: NdvrInteractor.servicesBaseUrls[TIVO_SERVICES.ChannelLogoImageHost],
        }).getUpcomingOffersForCollectionId(upcomingOffersParams);
    }

    public clearUpcomingOfferCache(): void {
        Services.getInstanceOf(UpcomingOffersService, {
            ...this._commonParameters(),
            basePath: NdvrInteractor.servicesBaseUrls[TIVO_SERVICES.UpcomingOffersService],
        }).clearUpcomingOfferCache();
    }

    /**
     * The method will return the object `TUpcomingOffersList` for the given  `CollectionId`.
     * @param upcomingOffersParams (TUpcomingOffersV2Params)
     * @returns `TUpcomingOffersList`
     */

    public getUpcomingOffersV2ForCollectionId(
        upcomingOffersParams: TUpcomingOffersV2Params,
    ): TiVoNetworkRequestModel<any> {
        return Services.getInstanceOf(UpcomingOffersService, {
            ...this._commonParameters(),
            basePath: NdvrInteractor.servicesBaseUrls[TIVO_SERVICES.UpcomingOffersService],
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).getUpcomingOffersV2ForCollectionId(upcomingOffersParams);
    }

    /**
     * The method will clear the cache`. */
    public clearUpcomingOfferV2Cache(): void {
        Services.getInstanceOf(UpcomingOffersService, {
            ...this._commonParameters(),
            basePath: NdvrInteractor.servicesBaseUrls[TIVO_SERVICES.UpcomingOffersService],
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).clearUpcomingOfferV2Cache();
    }

    /**
     * Below are the method related to cloudcore-previews
     */

    public getOnePassPreviewItemByUCId(
        onePassUCIdPreivewParams: TOnePassUCIdPreivewParams,
    ): Promise<TOnePassPreviewItem> {
        return Services.getInstanceOf(OnePassService, {
            ...this._commonParametersWithTimeZone(),
            basePath: NdvrInteractor.servicesBaseUrls[TIVO_SERVICES.OnePassService],
        }).getOnePassPreviewItemByUCId(onePassUCIdPreivewParams);
    }

    // eslint-disable-next-line class-methods-use-this
    public getPreviewForUCId(
        unifiedCollectionPreviewParams: TUnifiedCollectionPreviewParams,
    ): Promise<TUnifiedCollectionPreview> {
        return Services.getInstanceOf(PreviewsService, {
            ...this._commonParameters(),
            timeZoneName: DeviceModel.getTVProperties().getDefaultTimeZone(),
            imageHostBaseUrl: NdvrInteractor.servicesBaseUrls[TIVO_SERVICES.ImageHost],
            basePath: NdvrInteractor.servicesBaseUrls[TIVO_SERVICES.PreviewsService],
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        }).getPreviewForUCId(unifiedCollectionPreviewParams);
    }

    /**
     * Below are the method related to cloudcore-actions
     */

    public getUCScreenDetailsActions(
        actionsParams: TActionsParamsUCScreenDetails,
    ): Promise<TUnifiedCollectionScreenDetails> {
        return Services.getInstanceOf(ActionsService, {
            ...this._commonParameters(),
            basePath: NdvrInteractor.servicesBaseUrls[TIVO_SERVICES.ActionsService],
            timeZoneName: DeviceModel.getTVProperties().getDefaultTimeZone(),
        }).getUCScreenDetailsActions(actionsParams);
    }

    /**
     * The method will return the object `ContentScreenDetails` to fetch all 'recording' related
     * actions
     * @param recordingId (string) - The `recordingId` of the <episode/movie/game/match/event>
     * @param channelNumber (string) - [Optional Parameter]
     * @param networkId (string) - [Optional Parameter]
     * @returns `ContentScreenDetails`
     */
    public getContentScreenDetailsRecordingActions(
        actionsParams: TActionsContentScreenRecordingParams,
    ): Promise<ContentScreenDetails> {
        return Services.getInstanceOf(ActionsService, {
            ...this._commonParameters(),
            basePath: NdvrInteractor.servicesBaseUrls[TIVO_SERVICES.ActionsService],
            timeZoneName: DeviceModel.getTVProperties().getTimezone(),
        }).getContentScreenDetailsRecordingActions(actionsParams);
    }

    /**
     * The method will return the object `ContentScreenDetails` to fetch all 'content' related
     * actions
     * for the existing `recordingId`.
     * @param contentId (string) - The `contentId of the <episode/movie/game/match/event>
     * @param networkId (string) - [Optional Parameter]
     * @returns `ContentScreenDetails`
     */
    public getContentScreenDetailsContentActions(
        actionsParams: TActionsContentScreenContentIdParams,
    ): Promise<ContentScreenDetails> {
        return Services.getInstanceOf(ActionsService, {
            ...this._commonParameters(),
            basePath: NdvrInteractor.servicesBaseUrls[TIVO_SERVICES.ActionsService],
            timeZoneName: DeviceModel.getTVProperties().getTimezone(),
        }).getContentScreenDetailsContentActions(actionsParams);
    }

    /**
     * The method will return the object `ContentScreenDetails` to fetch all 'offer' related actions
     * @param offerId (string) - The `offerId of the <episode/movie/game/match/event>
     * @param channelNumber (string) - [Optional Parameter]
     * @param networkId (string) - [Optional Parameter]
     * @returns `ContentScreenDetails`
     */
    public getContentScreenDetailsOfferActions(
        actionsParams: TActionsContentScreenOfferParams,
    ): Promise<ContentScreenDetails> {
        return Services.getInstanceOf(ActionsService, {
            ...this._commonParameters(),
            basePath: NdvrInteractor.servicesBaseUrls[TIVO_SERVICES.ActionsService],
            timeZoneName: DeviceModel.getTVProperties().getTimezone(),
        }).getContentScreenDetailsOfferActions(actionsParams);
    }

    /**
     * Below are the method related to watch-stickiness
     */

    public setCollectionActivityStoreV2(
        collectionActivityStoreParams: TCollectionActivityStoreParams,
    ) {
        return Services.getInstanceOf(WatchStickinessService, {
            ...this._commonParameters(),
            basePath: NdvrInteractor.servicesBaseUrls[TIVO_SERVICES.WatchStickinessService],
        }).setCollectionActivityStoreV2(collectionActivityStoreParams);
    }
}
